import { IScriptConcept } from "@entities/aiWorkspace";
import { Button, Card } from "@shared/ui";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import React, { useState } from "react";
import styles from "./concepts.module.scss";
import { ScriptJobNotice } from "./ScriptJobNotice";
import { SCRIPT_DURATIONS, ScriptDraft } from "./types";

export interface ConceptCardProps {
  concept: IScriptConcept;
  index: number;
  isWriting: boolean;
  isSelected: boolean;
  isDisabled: boolean;
  onSelect: (concept: IScriptConcept) => void;
}

export function ConceptCard({
  concept,
  index,
  isWriting,
  isSelected,
  isDisabled,
  onSelect,
}: ConceptCardProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasRemainingContent = Boolean(
    concept.hook || concept.beats?.length || concept.summary
  );

  return (
    <Card
      className={`${styles.card} ${isSelected ? styles.cardSelected : ""} ${isWriting ? styles.cardWriting : ""}`}
    >
      <div
        className={styles.cardHeader}
        onClick={() => setIsExpanded((prev) => !prev)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsExpanded((prev) => !prev);
          }
        }}
      >
        <div className={styles.cardHeaderLeft}>
          <div className={styles.kickerRow}>
            <span className={styles.kicker}>Concept {index + 1}</span>
          </div>
          <h4 className={styles.title}>{concept.title}</h4>
        </div>
        <div className={styles.cardHeaderActions}>
          <Button
            size="small"
            type={isSelected ? "primary" : "default"}
            disabled={isDisabled}
            loading={isWriting}
            icon={isSelected ? <Check size={13} /> : undefined}
            onClick={(e) => {
              e.stopPropagation();
              onSelect(concept);
            }}
          >
            {isWriting
              ? "Writing script…"
              : isSelected
                ? "Selected"
                : "Use this concept"}
          </Button>
        </div>
      </div>

      <div className={styles.cardBody}>
        {concept.summary ? (
          <p className={`${styles.summary} ${!isExpanded ? styles.clamp : ""}`}>
            {concept.summary}
          </p>
        ) : null}

        {isExpanded ? (
          <div className={styles.remainingContent}>
            {concept.hook ? (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Hook</span>
                <p className={styles.hook}>{concept.hook}</p>
              </div>
            ) : null}
            {concept.beats?.length ? (
              <div className={styles.section}>
                <span className={styles.sectionLabel}>Key beats</span>
                <ul className={styles.beats}>
                  {concept.beats.map((beat, beatIndex) => (
                    <li key={`${concept._id}-${beatIndex}`}>{beat}</li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      {hasRemainingContent ? (
        <div className={styles.cardFooter}>
          <button
            type="button"
            className={styles.showMoreButton}
            onClick={(e) => {
              e.stopPropagation();
              setIsExpanded((prev) => !prev);
            }}
          >
            <span>{isExpanded ? "Show less" : "Show more"}</span>
            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
          </button>
        </div>
      ) : null}
    </Card>
  );
}

export function ConceptPicker({
  concepts,
  generating,
  writingId,
  selectedId,
  onSelect,
}: {
  concepts: IScriptConcept[];
  generating: boolean;
  writingId: string;
  selectedId: string;
  onSelect: (concept: IScriptConcept) => void;
}) {
  if (generating && !concepts.length) {
    return (
      <ScriptJobNotice
        tone="loading"
        title="Drafting concepts"
        detail="Using the current brief and output settings."
      />
    );
  }

  if (!concepts.length) return null;

  return (
    <div className={`${styles.list} ${generating ? styles.listPending : ""}`}>
      {concepts.map((concept, index) => {
        const isWriting = writingId === concept._id;
        const isSelected = selectedId === concept._id;
        const isDisabled = Boolean(writingId) || generating;

        return (
          <ConceptCard
            key={concept._id}
            concept={concept}
            index={index}
            isWriting={isWriting}
            isSelected={isSelected}
            isDisabled={isDisabled}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
}


function durationLabel(value: string) {
  return SCRIPT_DURATIONS.find((item) => item.value === value)?.label || value;
}

export function ChosenConcept({
  concept,
  draft,
  onChangeConcept,
}: {
  concept: IScriptConcept | null;
  draft: ScriptDraft;
  onChangeConcept: () => void;
}) {
  const [open, setOpen] = useState(false);
  const inputs = [
    ["Format", draft.settings.format],
    ["Duration", durationLabel(draft.settings.duration)],
    ["Tone", draft.settings.tone],
    ["Language", draft.settings.language],
  ] as const;

  return (
    <div className={styles.chosen}>
      <div className={styles.chosenHead}>
        <button
          type="button"
          className={styles.collapseToggle}
          aria-expanded={open}
          onClick={() => setOpen((value) => !value)}
        >
          <ChevronDown size={16} className={open ? styles.chevronOpen : styles.chevron} />
          <span className={styles.chosenCopy}>
            <p className={styles.kicker}>Concept</p>
            <p className={styles.chosenTitle}>{concept ? concept.title : "Saved script"}</p>
          </span>
        </button>
        <Button size="small" onClick={onChangeConcept}>
          {concept ? "Change concept" : "Choose a concept"}
        </Button>
      </div>
      {open ? (
        <div className={styles.collapseBody}>
          <p className={styles.kicker}>Brief</p>
          <p className={styles.summary}>{draft.brief.trim() || "No brief yet."}</p>
          <dl className={styles.inputList}>
            {inputs.map(([label, value]) => (
              <div key={label}>
                <dt>{label}</dt>
                <dd>{value}</dd>
              </div>
            ))}
          </dl>
          {concept ? (
            <>
              <p className={styles.kicker}>Selected concept</p>
              {concept.summary ? <p className={styles.summary}>{concept.summary}</p> : null}
              {concept.hook ? <p className={styles.hook}>{concept.hook}</p> : null}
              {concept.beats?.length ? (
                <ul className={styles.beats}>
                  {concept.beats.map((beat, index) => (
                    <li key={`${concept._id}-${index}`}>{beat}</li>
                  ))}
                </ul>
              ) : null}
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
